import { HttpHeaders } from '@angular/common/http';
import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BrandingConfigurationService } from '@ptl/brand';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { MedicationStateStructure, SelectedMedication } from 'src/app/angular-elements/medical-information/medication-information/reducers/medication.reducer';
import { DRUG_COVERAGE_STATUS_ENUM, IndividualDrugCoverageResponse, IndividualDrugItem } from 'src/app/shared/interfaces/drug-autocomplete-item.interface';
import { DrugDosageConstructor } from 'src/app/shared/interfaces/drug-dosage.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { DRXRetrieveSingleProductResponse, PlanDrugCoverageDrug } from '../../../../shared/interfaces/drx.interface';
import { DrxService } from '../../../medical-information/medication-information/services/drx.service';
import { MedicareAdvantage_and_PDP_PlanAttributes, PlanAttributes, SummaryView } from '../../interfaces/plan-attributes.interface';
import { MedicareAdvantage_and_PDP_Product, Product } from '../../interfaces/product.interface';
import { IndividualDrugCoverageService } from '../individual-drug-coverage/services/individual-drug-coverage.service';

interface PlanDrugDetails {
  drug: {
    rxcui: string;
    name: string;
    strength: string;
    route: string;
    full_name: string;
    rxterms_dose_form: string;
    rxnorm_dose_form: string;
  }
}

interface CoverageMapRenderable {
  name: string;
  coverage: DRUG_COVERAGE_STATUS_ENUM;
  full_name: string;
  genericInfo: {
    name: string | null | 'LOOKUP FAILED',
    full_name: string | null | 'LOOKUP FAILED'
  };
}

@Component({
    selector: 'app-medical-details-modal',
    templateUrl: './medical-details-modal.component.html',
    styleUrls: ['./medical-details-modal.component.scss'],
    standalone: false
})
export class MedicalDetailsModalComponent implements OnInit {

  coverageMappedToName: CoverageMapRenderable[];

  showLoadingIndicator = true;

  constructor(
    private dialogRef: MatDialogRef<MedicalDetailsModalComponent>,
    private reducerUtilityService: ReducerUtilityService,
    private brandService: BrandingConfigurationService,
    private httpClient: HTTPWrapperContext,
    private indDrugService: IndividualDrugCoverageService,
    private drxService: DrxService,
    @Inject(MAT_DIALOG_DATA) public data: {
      type: string;
      seniorDrugCoverage: any;
      individualDrugCoverage: IndividualDrugCoverageResponse;
      plan: Product<PlanAttributes, SummaryView>;
      providerCoverage: any;
    }
  ) { }

  async ngOnInit() {
    if (this.data?.individualDrugCoverage) {
      await this.loadIndividualDrugs();
    }

    if (this.data?.seniorDrugCoverage) {
      await this.loadSeniorDrugs();
    }

    this.showLoadingIndicator = false;
  }

  closeModal() {
    this.dialogRef.close();
  }

  getCoverageHumanFriendlyLabel(label) {
    switch (label) {
      case DRUG_COVERAGE_STATUS_ENUM.COVERED:
        return 'Covered';
      case DRUG_COVERAGE_STATUS_ENUM.GENERIC_COVERED:
        return 'Generic Alternative Covered';
      case DRUG_COVERAGE_STATUS_ENUM.NOT_COVERED:
        return 'Not Covered';
      case DRUG_COVERAGE_STATUS_ENUM.DATA_NOT_PROVIDED:
      default:
        return 'Data Not Provided';
    }
  }

  async loadSeniorDrugs() {
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
    if (medState?.medicationForm?.selectedMedications?.length) {    
      this.coverageMappedToName = [];
      const plan = this.data.plan as MedicareAdvantage_and_PDP_Product;
      const drugResponse: DRXRetrieveSingleProductResponse = await this.drxService.loadDrugCoverageDetails(plan);
        
      drugResponse.PlanDrugCoverage.forEach((drug: PlanDrugCoverageDrug) => {
        this.coverageMappedToName.push({
          coverage: drug.TierNumber > 0 ? DRUG_COVERAGE_STATUS_ENUM.COVERED : DRUG_COVERAGE_STATUS_ENUM.NOT_COVERED,
          name: drug.LabelName
        } as CoverageMapRenderable);
      });
    }
  }

  async loadIndividualDrugs() {
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
    if (medState?.individualMedications?.length) {

      const individualMeds = medState.individualMedications;
      const hiosId = this.indDrugService.getHiosIdFromProduct(this.data?.plan);

      const planSpecificDrugStatusArray = this.data.individualDrugCoverage.coverage.filter(i => i.plan_id === hiosId);

      /** Loop through drugs, if there's a generic equivalent, fetch the generic name - return async and sync results */
      this.coverageMappedToName = await Promise.all(
        planSpecificDrugStatusArray.map(async (coveredDrug): Promise<CoverageMapRenderable> => {
          const drugDataFromLookup = individualMeds.find(drug => drug.rxcui === coveredDrug.rxcui);

          if (drugDataFromLookup) {
            return await this.getGenericDrugChecker(coveredDrug, drugDataFromLookup, hiosId);
          }
          else {
            return null;
          }
        })
      );
    }
  }

  getGenericDrugChecker(coveredDrug, drugDataFromLookup, planId): Promise<CoverageMapRenderable> {
    return new Promise((resolve, reject) => {
      if (coveredDrug.generic_rxcui) {
        this.getGenericLabel(planId, coveredDrug.generic_rxcui).then(
          genericData => resolve({
            coverage: coveredDrug.coverage,
            name: drugDataFromLookup.name,
            full_name: drugDataFromLookup.full_name,
            genericInfo: {
              name: genericData.drug.name,
              full_name: genericData.drug.full_name
            }
          }),
          fail => resolve({
            coverage: coveredDrug.coverage,
            name: drugDataFromLookup.name,
            full_name: drugDataFromLookup.full_name,
            genericInfo: {
              name: 'LOOKUP FAILED',
              full_name: 'LOOKUP FAILED'
            }
          })
        );
      } 
      else {
        resolve({
          coverage: coveredDrug.coverage,
          name: drugDataFromLookup.name,
          full_name: drugDataFromLookup.full_name,
          genericInfo: null
        });
      }
    });
  }

  async getGenericLabel(planId: string, genericRxcui: string): Promise<PlanDrugDetails> {
    const payload = {
      planids: planId,
      searchtext: genericRxcui,
      year: (new Date()).getFullYear() //  this.data.plan.attributes.
    };

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v1/druglookup/plandrug',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: payload,
      strategyConfigs: { forceModernUrl: true }
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    const genericResponse = await this.httpClient.makeRequest<PlanDrugDetails>(requestConfigImplementation);
    return genericResponse;
  }
}
